import { Routes } from "@angular/router";
import { AuthGuard } from "./guards/auth-guard/auth.guard";

export const routes: Routes = [
  { path: "", pathMatch: "full", redirectTo: "dashboard" },

  // Dashboard & Login
  {
    path: "dashboard",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./pages/dashboard/dashboard.page").then(
        (page) => page.DashboardPage,
      ),
  },
  {
    path: "login",
    loadComponent: () =>
      import("./pages/login/login.page").then((page) => page.LoginPage),
  },

  // Calendar
  {
    path: "calendar",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./pages/dashboard/calendar/calendar/calendar.page").then(
        (page) => page.CalendarPage,
      ),
  },

  // Profile & Check-in
  {
    path: "profile-view",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./pages/profile-view/profile-view.page").then(
        (page) => page.ProfileViewPage,
      ),
  },
  {
    path: "check-in",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./pages/check-in/check-in.page").then((page) => page.CheckInPage),
  },

  // Assessment Routes
  {
    path: "assessment",
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./pages/assessment/assessment.page").then(
            (page) => page.AssessmentPage,
          ),
      },
      {
        path: "material-assessment/:id",
        loadChildren: () =>
          import("./pages/assessment/mm-assessment/mm-assessment.routes").then(
            (page) => page.routes,
          ),
      },
      {
        path: "asset-assessment/:id",
        loadChildren: () =>
          import("./pages/assessment/asset-assessment/asset-assessment.routes").then(
            (page) => page.routes,
          ),
      },
    ],
  },

  // Action Management
  {
    path: "action-management",
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./pages/action-management/action-management.page").then(
            (page) => page.ActionManagementPage,
          ),
      },
      {
        path: "finding-info/:assessmentId/:findingId",
        loadComponent: () =>
          import("./pages/action-management/finding-info/finding-info.page").then(
            (page) => page.FindingInfoPage,
          ),
      },
      {
        path: "action-info/:assessmentId/:findingId/:actionId",
        loadComponent: () =>
          import("./pages/action-management/action-info/action-info.page").then(
            (page) => page.ActionInfoPage,
          ),
      },
    ],
  },

  // Energy Isolation
  {
    path: "energy-isolation",
    canActivate: [AuthGuard],
    children: [
      {
        path: "ei-pdf/:id",
        loadComponent: () =>
          import("./pages/energy-isolation/ei-pdf/ei-pdf.page").then(
            (page) => page.EiPdfPage,
          ),
      },
      {
        path: "add-new",
        loadComponent: () =>
          import("./pages/energy-isolation/add-new/add-new.page").then(
            (page) => page.AddNewPage,
          ),
      },
      {
        path: "edit/:id",
        loadComponent: () =>
          import("./pages/energy-isolation/add-new/add-new.page").then(
            (page) => page.AddNewPage,
          ),
      },
      {
        path: "",
        loadComponent: () =>
          import("./pages/energy-isolation/energy-isolation.page").then(
            (page) => page.EnergyIsolationPage,
          ),
      },
      {
        path: "to-review",
        loadComponent: () =>
          import("./pages/energy-isolation/to-review/to-review.page").then(
            (page) => page.ToReviewPage,
          ),
      },
      {
        path: "to-approve",
        loadComponent: () =>
          import("./pages/energy-isolation/to-approve/to-approve.page").then(
            (page) => page.ToApprovePage,
          ),
      },
      {
        path: "user-management",
        loadComponent: () =>
          import("./pages/energy-isolation/user-management/user-management.page").then(
            (page) => page.UserManagementPage,
          ),
      },
      {
        path: "change-language",
        loadComponent: () =>
          import("./pages/energy-isolation/change-language/change-language.page").then(
            (page) => page.ChangeLanguagePage,
          ),
      },
      {
        path: "my-draft-procedure",
        loadComponent: () =>
          import("./pages/energy-isolation/draft-procedure/draft-procedure.page").then(
            (page) => page.DraftProcedurePage,
          ),
      },
      {
        path: "user-management/add-user",
        loadComponent: () =>
          import("./pages/energy-isolation/user-management/add-user/add-user.page").then(
            (page) => page.AddUserPage,
          ),
      },
      {
        path: "support-activity",
        loadComponent: () =>
          import("./pages/energy-isolation/support-activity/support-activity.page").then(
            (page) => page.SupportActivityPage,
          ),
      },
      {
        path: "archive-procedure",
        loadComponent: () =>
          import("./pages/energy-isolation/archive-procedure/archive-procedure.page").then(
            (page) => page.ArchiveProcedurePage,
          ),
      },
      {
        path: "user-management/edit-user/:id",
        loadComponent: () =>
          import("./pages/energy-isolation/user-management/edit-user/edit-user.page").then(
            (page) => page.EditUserPage,
          ),
      },
      {
        path: "energy-isolation-details/:id",
        loadComponent: () =>
          import("./pages/energy-isolation/energy-isolation-details/energy-isolation-details.page").then(
            (page) => page.EnergyIsolationDetailsPage,
          ),
      },
    ],
  },

  // Settings
  {
    path: "settings",
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/settings/settings.routes").then((page) => page.routes),
  },

  // Admin
  {
    path: "admin",
    canActivate: [AuthGuard],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./pages/admin/admin.page").then((page) => page.AdminPage),
      },
      {
        path: "performance",
        loadComponent: () =>
          import("./pages/admin/performance/performance.page").then(
            (page) => page.PerformancePage,
          ),
      },
      {
        path: "reports-download",
        loadComponent: () =>
          import("./pages/admin/reports-download/reports-download.page").then(
            (page) => page.ReportsDownloadPage,
          ),
      },
      {
        path: "guidance/add-guidance",
        loadComponent: () =>
          import("./pages/admin/guidance/add-guidance/add-guidance.page").then(
            (page) => page.AddGuidancePage,
          ),
      },
      {
        path: "custom-reports",
        loadComponent: () =>
          import("./pages/admin/reports-download/custom-reports/custom-reports.page").then(
            (page) => page.CustomReportsPage,
          ),
      },
      {
        path: "guidance/add-defects",
        loadComponent: () =>
          import("./pages/admin/guidance/add-defect/add-defects/add-defects.page").then(
            (page) => page.AddDefectsPage,
          ),
      },
      {
        path: "control-inventory",
        loadComponent: () =>
          import("./components/admin/control-inventory/control-inventory.component").then(
            (page) => page.ControlInventoryComponent,
          ),
      },
      {
        path: "add-mm-control-inventory",
        loadComponent: () =>
          import("./components/admin/control-inventory/add-mm-control-inventory/add-mm-control-inventory.component").then(
            (page) => page.AddMMControlInventoryComponent,
          ),
      },
      {
        path: "master-data/asset-master",
        loadChildren: () =>
          import("./pages/admin/master-data/asset-master/asset-master.routes").then(
            (routes) => routes.routes,
          ),
      },
      {
        path: "master-data/asset-master/:id",
        loadComponent: () =>
          import("./pages/admin/master-data/asset-master/asset-master-details/asset-master-details.page").then(
            (m) => m.AssetMasterDetailsPage,
          ),
      },
      {
        path: "master-data/site-master",
        loadChildren: () =>
          import("./pages/admin/master-data/site-master/site-master.routes").then(
            (routes) => routes.routes,
          ),
      },
      {
        path: "master-data/energy-isolation-master",
        loadComponent: () =>
          import("./pages/admin/master-data/ei-master/ei-master.page").then(
            (page) => page.EiMasterPage,
          ),
      },
      {
        path: 'master-data',
        loadComponent: () =>
          import("./pages/admin/master-data/material-master/material-master.component").then(
            (page) => page.MaterialMasterComponent,
          ),
      },
      {
        path: "master-data/site-security-mapping",
        loadComponent: () =>
          import("./pages/admin/master-data/site-security-mapping/site-security-mapping.page").then(
            (page) => page.SiteSecurityMappingPage,
          ),
      },
    ],
  },

  // Training
  {
    path: "training",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./components/admin/training/training.component").then(
        (c) => c.TrainingComponent,
      ),
  },
  {
    path: "training/local_trainings",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./components/admin/training/local-trainings.component").then(
        (c) => c.LocalTrainingsComponent,
      ),
  },
  {
    path: "training/detail/:id",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./components/admin/training/training-detail.component").then(
        (c) => c.TrainingDetailComponent,
      ),
  },

  // Connections
  {
    path: "connections",
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./pages/connections/connection.routes").then(
        (routes) => routes.routes,
      ),
  },
  {
    path: "material-management",
    loadComponent: () =>
      import("./pages/admin/material-management/material-management.page").then(
        (m) => m.MaterialManagementPage,
      ),
  },
  {
    path: "cannot-find-asset",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./pages/cannot-find-asset/cannot-find-asset.page").then(
        (page) => page.CannotFindAssetPage,
      ),
  },

  {
    path: 'hse-report-dashboard',
    loadComponent: () =>
      import('./pages/hse-report-dashboard/hse-report-dashboard.page').then(
        page => page.HseReportDashboardPage
      )
  },


  // Mapping (map-first view of Performance data)
  {
    path: 'mapping',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/mapping/mapping.page').then(m => m.MappingPage),
  },

  // Performance Dashboard
  {
    path: 'performance-dashboard',
    loadComponent: () =>
      import('./pages/hse-report-dashboard/hse-report-dashboard.page').then(m => m.HseReportDashboardPage),
  },
];
